<template>
  <div class="bg-gray-21 px-3">
    <div class="d-flex align-items-center mb-4 ml-n3">
      <circle-icon
        shadow
        size="31px"
        icon-size="20px"
        color="#fff"
        icon-color="#fff"
      >
        <template #icon>
          <img src="@/assets/icons/folder-grid.svg" />
        </template>
      </circle-icon>
      <span class="color-black-1 font-16 font-weight-bold ml-2">
        {{ $t('DocumentsRequiredForInvestigation') }}
      </span>
    </div>
    <div
      v-for="(item, index) in documents"
      :key="`doc-item-${index}`"
      class="row bg-white p-3 rounded mb-2"
    >
      <div class="col-4">
        <div class="color-gray-18 font-14">{{ $t('DocType') }}:</div>
        <div>
          <span
            v-if="item.documentCategoryCode"
            class="color-black font-14 font-weight-bold"
          >
            {{ item.documentCategoryCode }} -
          </span>
          <span class="color-black font-14">
            {{ item.documentCategoryName }}
          </span>
        </div>
      </div>
      <div class="col">
        <div v-if="item.filename">
          <div class="color-gray-18 font-14">{{ $t('Document') }}:</div>
          <span class="color-black font-14">
            {{ item.filename }}
          </span>
        </div>
      </div>
      <div class="col d-flex align-items-center">
        <span
          v-if="!item.documentId && !isReadOnly"
          class="color-red-1 font-14"
        >
          {{ $t('Required') }}*
        </span>
      </div>
      <div class="col d-flex align-items-center">
        <b-form-checkbox
          v-if="!item.hiddenPublic && !isReadOnly"
          :disabled="item.filename !== undefined"
          v-model="item.isPublic"
          class="d-block"
        >
          <span class="color-black-3 font-14 font-weight-bold">
            {{ $t('IsPublic') }}
          </span>
        </b-form-checkbox>
      </div>
      <div class="col d-flex align-items-center justify-content-end">
        <input
          :id="`fileUpload${item.id}`"
          :ref="`refFileUpload${item.id}`"
          v-show="false"
          accept=".pdf"
          type="file"
          @change="updateFileChange($event, item)"
        />
        <action-button
          v-if="!item.documentId && !isReadOnly"
          :text="$t('ClickToUploadDocument')"
          class="warning outline-warning px-4 mr-2"
          i="fal fa-cloud-upload"
          @click="onAddFileHandler(item)"
        />
        <action-button
          v-if="item.documentId && !isReadOnly"
          class="btn-outline-success delete-action mr-2"
          i="fal fa-minus-circle"
          @click="onDeleteHandler(item)"
        />
        <action-button
          v-if="item.documentId"
          class="warning px-4 mr-2 download-action"
          i="fa fa-cloud-download"
          @click="onDownloadHandler(item.documentId)"
        />
      </div>
    </div>
  </div>
</template>

<script>
//libs
import ActionButton from 'ActionButton';

//components
import { CircleIcon } from '@/components/common';

//utils
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin';

//services
import {
  downloadItemAttachment,
  saveItemAttachment,
  removeItemAttachment,
} from '@/services/app.api.js';
import { getIsLockedDocuments } from '@/modules/tickets/api.js';
import objectToFormData from 'object-to-formdata';
import { i18n } from '@/i18n';
export default {
  name: 'Documents',
  mixins: [alertMixin],
  components: {
    ActionButton,
    CircleIcon,
  },
  props: {
    itemId: {
      type: Number,
      required: true,
    },
    documents: {
      type: Array,
      default: () => [],
    },
    isReadOnly: {
      type: Boolean,
      default: false,
    },
  },
  methods: {
    async onDownloadHandler(documentId) {
      const { data } = await getIsLockedDocuments(documentId);
      const isLocked = data;

      if (isLocked) {
        this.ShowToast(i18n.t('Alert'), `${i18n.t('LockedFiles')}.`, 'warning');
        return;
      }
      if (documentId) {
        await downloadItemAttachment(documentId).catch((error) => {
          this.ShowToast('', error.response.data.message, 'error');
        });
      }
    },
    onAddFileHandler(item) {
      document.getElementById(`fileUpload${item.id}`).click();
    },
    onRefreshHandler() {
      this.$emit('on-refresh-document');
    },
    async updateFileChange(event, item) {
      if (event.target.files && event.target.files.length > 0) {
        const selectedFile = Array.from(event.target.files)?.map(
          (file) => file
        )[0];

        // Obtener la extensión del archivo
        const fileExtension = selectedFile.name.split('.').pop().toLowerCase();
        const allowedExtensions = ['pdf', 'jpg'];

        // Validar que la extensión esté permitida
        if (!allowedExtensions.includes(fileExtension)) {
          this.ShowToast(i18n.t('Error'), `${i18n.t('AlertOnlyPdfAttachment')}.`, 'error'); 
          event.target.value = ''; 
          return;
        }

        let formData = objectToFormData(
          {
            itemId: this.itemId,
            fileExtension: fileExtension,
            categoryId: item.id,
            fileName: selectedFile.name,
            file: selectedFile,
            isPublic: item.isPublic,
          },
          {
            indices: true,
            nullsAsUndefineds: true,
          }
        );

        await saveItemAttachment(formData);
        this.onRefreshHandler();
      }

      event.target.value = '';
    }, 
    async onDeleteHandler(item) {
      if (item.documentId) {
        const answer = await this.ShowCancelModal(
          'Remover Documento',
          '¿Está seguro que desea remover el documento?',
          'Si',
          'No'
        );
        if (answer) {
          await removeItemAttachment(item.documentId);
          this.ShowToast(
            'Proceso exitoso',
            'El documento ha sido removido exitosamente.',
            'success'
          );
          this.onRefreshHandler();
        }
      }
    },
  },
};
</script>

<style scoped lang="scss">
::v-deep .delete-action {
  border-radius: 4px;
  border: 1px solid #0f556a;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  &:hover {
    background: unset;
    color: unset;
  }
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}

::v-deep .download-action {
  border-radius: 4px;
  width: 38px;
  height: 38px;
  display: flex;
  justify-content: center;
  align-items: center;
  i {
    font-size: 20px;
    margin-right: 0 !important;
  }
}
</style>
